import React from 'react';
import { Button, Text, VStack } from '@chakra-ui/react';
import { PendingActionMode, PendingActionType, PendingOption } from '@/client';
import { getConfirmOptionCopy, isPrimaryAction } from './chatPendingActionCopy';

interface ChatPendingActionOptionProps {
	option: PendingOption;
	pendingType: PendingActionType;
	mode: PendingActionMode;
	disabled: boolean;
	onSelect: () => void;
}

/**
 * Renders one `PendingOption`. The two modes need different shapes, not just different styling:
 * a `confirm` choice is a short verb ("Save", "Cancel") sized as a pill; a `select` choice carries
 * two lines of DATA (the document name or the correction's own wording, plus where it applies) and
 * needs room for that — see docs/local/chat-feature.md §C2 on why `label` is copy in one mode and
 * data in the other.
 */
const ChatPendingActionOption: React.FC<ChatPendingActionOptionProps> = (props: ChatPendingActionOptionProps) => {
	const { option, pendingType, mode, disabled, onSelect } = props;

	if (mode === 'select') {
		return (
			<Button
				type="button"
				variant="outline"
				justifyContent="flex-start"
				textAlign="left"
				h="auto"
				py={2.5}
				px={3}
				whiteSpace="normal"
				disabled={disabled}
				onClick={onSelect}
			>
				<VStack align="start" gap={0.5} w="full">
					<Text textStyle="label">{option.label}</Text>
					{option.detail && (
						<Text textStyle="micro" color="fg.muted">
							{option.detail}
						</Text>
					)}
				</VStack>
			</Button>
		);
	}

	const label = getConfirmOptionCopy(pendingType, option.action, option.label);

	return (
		<Button
			type="button"
			variant={isPrimaryAction(option.action) ? 'solid' : 'outline'}
			colorPalette={isPrimaryAction(option.action) ? 'brand' : 'gray'}
			size="sm"
			rounded="full"
			disabled={disabled}
			onClick={onSelect}
		>
			{label}
		</Button>
	);
};

export default ChatPendingActionOption;
